/**
 * DoorWalkTransition — lazy, failure-guarded entry point for the "Walk Through
 * the Door" match-start choreography (Own Tavern plan §3).
 *
 * Deliberately three.js-FREE so the heavy {@link DoorWalkTransitionScene}
 * (which statically imports `three` + `@tt/shell-3d`) only downloads the first
 * time a match actually starts, never weighing down first paint. Use THIS from
 * Room.tsx, never the scene module directly.
 *
 * Fallback: prefers-reduced-motion is caught HERE (the chunk never even loads)
 * and `onDone` fires next tick, so the existing fade path is untouched. Any
 * WebGL/chunk failure inside the scene (or its ErrorBoundary) likewise resolves
 * to `onDone` — GameStage + UniversalMatchIntro are already live underneath.
 */
import { Suspense, lazy, useEffect } from 'react';
import type { LobbyPlayerView } from '@tt/lobby';
import { Lobby3DBoundary } from './Lobby3DBoundary';
import type { TavernConfig } from './tavernDecor';

const DoorWalkTransitionScene = lazy(() => import('./DoorWalkTransitionScene'));

export interface DoorWalkTransitionProps {
  players: LobbyPlayerView[];
  meCharId?: string;
  matchId?: string;
  /** Server GO instant (local clock). The walk must fade out before this. */
  startsAt: number;
  tavernConfig?: TavernConfig;
  /** Fired once the walk completes or on any bail — Room clears the overlay. */
  onDone: () => void;
}

/** Fire `onDone` on the next tick, then render nothing. Used when the walk is
 *  skipped (reduced motion) so the caller's completion contract still holds. */
function SkipWalk({ onDone }: { onDone: () => void }) {
  useEffect(() => {
    const t = window.setTimeout(onDone, 0);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function reducedMotion(): boolean {
  try {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches === true;
  } catch { return false; }
}

export function DoorWalkTransition({ players, meCharId, matchId, startsAt, tavernConfig, onDone }: DoorWalkTransitionProps) {
  if (reducedMotion()) return <SkipWalk onDone={onDone} />;
  return (
    // If the chunk or WebGL scene throws, the boundary swallows it; a
    // sibling SkipWalk guarantees onDone still fires so the match isn't
    // trapped behind a dead overlay.
    <Lobby3DBoundary fallback={<SkipWalk onDone={onDone} />}>
      <Suspense fallback={null}>
        <DoorWalkTransitionScene
          players={players}
          startsAt={startsAt}
          onDone={onDone}
          {...(meCharId ? { meCharId } : {})}
          {...(matchId ? { matchId } : {})}
          {...(tavernConfig ? { tavernConfig } : {})}
        />
      </Suspense>
    </Lobby3DBoundary>
  );
}
